import PropTypes from "prop-types";
import React from "react";

/* -------------------------------------------------------------------------
 * Small shared building blocks reused across the bottom-sheet modals
 * (Voucher Detail, Redeem flow, Connect Account, Restaurant Detail...).
 * Styled entirely through the shared "mc-*" classes in
 * bottom-sheet-modals.scss so every modal stays visually consistent.
 * ---------------------------------------------------------------------- */

export const StepHeader = ({ eyebrow, title, subtitle, titleId }) => (
  <div className="mc-step-header">
    {eyebrow && <p className="mc-eyebrow">{eyebrow}</p>}
    <h2 className="mc-title mc-title--center" id={titleId}>
      {title}
    </h2>
    {subtitle && <p className="mc-description mc-description--center">{subtitle}</p>}
  </div>
);

StepHeader.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  titleId: PropTypes.string,
};

StepHeader.defaultProps = {
  eyebrow: undefined,
  subtitle: undefined,
  titleId: undefined,
};

export const Divider = () => <div className="mc-divider" />;

export const NumberedSteps = ({ steps }) => (
  <ol className="mc-step-list">
    {steps.map((step, index) => (
      <li key={step} className="mc-step">
        <span className="mc-step__num">{index + 1}</span>
        <span className="mc-step__text">{step}</span>
      </li>
    ))}
  </ol>
);

NumberedSteps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const InfoRow = ({ children, center }) => (
  <div className={`mc-info-row ${center ? "mc-info-row--center" : "mc-info-row--split"}`}>
    {children}
  </div>
);

InfoRow.propTypes = {
  children: PropTypes.node.isRequired,
  center: PropTypes.bool,
};

InfoRow.defaultProps = {
  center: false,
};

export const SuccessIcon = ({ icon, tone }) => (
  <div className={`mc-success-icon mc-success-icon--${tone}`}>
    <img src={icon} alt="" />
  </div>
);

SuccessIcon.propTypes = {
  icon: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["green", "orange"]),
};

SuccessIcon.defaultProps = {
  tone: "green",
};

/* -------------------------------------------------------------------------
 * StatusPill — small centered "In Progress" / "Dine In" style label that
 * sits under the hero icon on detail sheets (Activity Detail, Game
 * Activity Detail).
 * ---------------------------------------------------------------------- */

export const StatusPill = ({ label }) => (
  <div className="mc-hero-status-wrap">
    <span className="mc-pill mc-pill--tag">{label}</span>
  </div>
);

StatusPill.propTypes = {
  label: PropTypes.string.isRequired,
};

/* -------------------------------------------------------------------------
 * PointsBadge — large colored "+85 pts" style badge (Activity Detail).
 * ---------------------------------------------------------------------- */

export const PointsBadge = ({ value, tone }) => {
  const positive = value >= 0;
  return (
    <div className="mc-points-badge-wrap">
      <span className={`mc-points-badge mc-points-badge--${tone}`}>
        <strong>
          {positive ? "+" : ""}
          {value}
        </strong>{" "}
        pts
      </span>
    </div>
  );
};

PointsBadge.propTypes = {
  value: PropTypes.number.isRequired,
  tone: PropTypes.oneOf(["green", "red"]),
};

PointsBadge.defaultProps = {
  tone: "green",
};

/* -------------------------------------------------------------------------
 * StepChips — equal-width week/milestone chips supporting three states:
 * done (check), failed (x), pending (flame). Used by Game Activity Detail.
 * ---------------------------------------------------------------------- */

export const StepChips = ({ steps, doneIcon, failedIcon, pendingIcon }) => (
  <div className="mc-step-chips" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
    {steps.map((step) => {
      const state = step.state || (step.done ? "done" : "pending");
      const icon = state === "done" ? doneIcon : state === "failed" ? failedIcon : pendingIcon;
      return (
        <div key={step.label} className={`mc-step-chip mc-step-chip--${state}`}>
          <span className="mc-step-chip__icon">
            {icon && <img src={icon} alt="" />}
          </span>
          <span className="mc-step-chip__label">{step.label}</span>
        </div>
      );
    })}
  </div>
);

StepChips.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      state: PropTypes.oneOf(["done", "failed", "pending"]),
      done: PropTypes.bool,
    })
  ).isRequired,
  doneIcon: PropTypes.string,
  failedIcon: PropTypes.string,
  pendingIcon: PropTypes.string,
};

/* -------------------------------------------------------------------------
 * Timeline — vertical dotted history log (Game Activity Detail's
 * "Streak History").
 * ---------------------------------------------------------------------- */

export const Timeline = ({ items, isVisitType = false }) => (
  <div className="mc-timeline">
    {items.map((item, index) => (
      <div key={`${item.week}-${index}`} className={`mc-timeline-item mc-timeline-item--${item.tone}`}>
        <span className="mc-timeline-item__dot" />
        <div className="mc-timeline-item__content">
          {/* Left side: Week, Status, and Message */}
          <div className="mc-timeline-item__left">
            <div className="mc-timeline-item__header">
              <span className="mc-timeline-item__week">{isVisitType ? item.message : `Week ${item.week}`}</span>
              <span className={`mc-timeline-item__status mc-timeline-item__status--${item.tone}`}>
                {item.status}
              </span>
            </div>
            {isVisitType && item.address && (
              <span className="mc-timeline-item__address">{item.address}</span>
            )}
          </div>
          {/* Right side: Points and Date */}
          <div className="mc-timeline-item__right">
            <span className="mc-timeline-item__points">{item.points}</span>
            <span className="mc-timeline-item__date">{item.date}</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

Timeline.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      week: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      tone: PropTypes.oneOf(["green", "red", "blue", "grey"]).isRequired,
      points: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    })
  ).isRequired,
};

/* -------------------------------------------------------------------------
 * StatsRow — row of label/value stat columns (Started / Best Streak /
 * Reward / Deadline).
 * ---------------------------------------------------------------------- */

export const StatsRow = ({ stats }) => (
  <div className="mc-stats-row">
    {stats.map((stat) => (
      <div key={stat.label} className="mc-stat">
        <span className="mc-stat__label">{stat.label}</span>
        <span className="mc-stat__value">{stat.value}</span>
      </div>
    ))}
  </div>
);

StatsRow.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
};

/* -------------------------------------------------------------------------
 * SectionRow — section label with a trailing meta value on the same line
 * ("Your Progress" ... "02/04").
 * ---------------------------------------------------------------------- */

export const SectionRow = ({ label, meta }) => (
  <div className="mc-section-row">
    <p className="mc-section-label">{label}</p>
    <span className="mc-section-meta">{meta}</span>
  </div>
);

SectionRow.propTypes = {
  label: PropTypes.string.isRequired,
  meta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};